/**
 * `useChatLevel` — the SPA half of the concierge FAST/NORMAL/DEEP seam
 * (P4; contract `ai-docs/level-system-p3-seam-contract.md` v1.3, methods as
 * landed in `gateway-methods/chat-level.ts`).
 *
 * Wire contract (bound to the LANDED gateway, not the plan sketch):
 *  - every call takes `{ sessionKey }`;
 *  - `lx.set_chat_level` adds `{ level, originSurface: "lexide" }` (req 9 —
 *    origin is recorded, never guessed);
 *  - reads return `{ ok:true, readback }`; the levels listing returns
 *    `{ ok:true, levels }`;
 *  - a DEFINITIVE write break is a structured RESULT
 *    (`{ok:false, definitive:true, reason, detail}`) — routed to the L-9
 *    gate via `definitiveFail`, NEVER a retry toast. Thrown errors are the
 *    retryable class (toast + keep state).
 *
 * State semantics (the honesty rules live in `lib/chat-level.ts`):
 *  - `null` = still loading (chip renders nothing — ModelBadge's fail-soft);
 *  - `{kind:"unknown"}` = the CALL failed — chat stays usable, the chip says
 *    unknown; it is never collapsed onto a level (miniplan §3(b)).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  isDefinitiveFail,
  isPinNotApplied,
  reduceChatLevelReadback,
  type ChatLevelUiState,
  type ConciergeMode,
  type DefinitiveFail,
  type PinNotApplied,
  type ResolvedRef,
} from "@/lib/chat-level";

/** One row of `lx.get_chat_levels` — informational, for the popover
 *  (the FATAL verdict arrives pre-aggregated on `getLevel`, never derived
 *  from these rows — contract AC-6). */
export interface ChatLevelRow {
  level: ConciergeMode;
  alias: string | null;
  resolved: ResolvedRef | null;
  unavailableReason: string | null;
}

function rpc() {
  const client = useGatewayStore.getState().rpc;
  if (!client) throw new Error("gateway not connected");
  return client;
}

function reduceRows(payload: unknown): ChatLevelRow[] {
  if (typeof payload !== "object" || payload === null) return [];
  const rows = (payload as { levels?: unknown }).levels;
  if (!Array.isArray(rows)) return [];
  const out: ChatLevelRow[] = [];
  for (const r of rows) {
    if (typeof r !== "object" || r === null) continue;
    const o = r as Record<string, unknown>;
    if (o.level !== "FAST" && o.level !== "NORMAL" && o.level !== "DEEP") continue;
    const resolved =
      typeof o.resolved === "object" && o.resolved !== null
        ? (o.resolved as ResolvedRef)
        : null;
    out.push({
      level: o.level,
      alias: typeof o.alias === "string" ? o.alias : null,
      resolved,
      unavailableReason:
        typeof o.unavailableReason === "string" ? o.unavailableReason : null,
    });
  }
  return out;
}

/** Backoff for a FAILED level read, in ms. Covers the measured ~27s window in
 *  which a freshly recreated container is up but not yet SERVING (i-ter): a page
 *  loaded into that tail reads once, fails, and — before this — sat on
 *  "Nível indisponível" forever, because the only recovery was keyed on a
 *  gateway transition INTO connected and the socket never transitioned again. */
const LEVEL_RETRY_MS = [2000, 5000, 10_000, 20_000] as const;

export function useChatLevel(sessionKey: string | null) {
  const [state, setState] = useState<ChatLevelUiState | null>(null);
  const [rows, setRows] = useState<ChatLevelRow[] | null>(null);
  const [definitiveFail, setDefinitiveFail] = useState<DefinitiveFail | null>(null);
  /** ⚖ v1.7 §D (ii) — the last write-path rejection (pin verified NOT applied).
   *  Cleared by any subsequent successful pick. */
  const [notApplied, setNotApplied] = useState<PinNotApplied | null>(null);
  const [busy, setBusy] = useState(false);
  // Retry bookkeeping for a failed read (i-ter). `attempt` indexes
  // LEVEL_RETRY_MS; the timer is cleared on unmount and on every sessionKey
  // change, so a stale session can never re-read into a new one.
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttempt = useRef(0);
  const clearRetry = useCallback(() => {
    if (retryTimer.current !== null) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    if (!sessionKey) return;
    try {
      const res = await rpc().call<{ ok: true; readback: unknown }>(
        "lx.get_chat_level",
        { sessionKey },
      );
      if (!alive.current) return;
      // A read that ARRIVES ends the retry sequence, whatever it says.
      clearRetry();
      retryAttempt.current = 0;
      setState(reduceChatLevelReadback(res?.readback));
    } catch {
      // The call itself failed — requirement 1's third outcome. Chat stays
      // usable; the chip reads unknown. NEVER a level.
      if (!alive.current) return;
      const delay = LEVEL_RETRY_MS[retryAttempt.current];
      // `retrying` is true only while a retry is actually SCHEDULED. Once the
      // backoff is exhausted the state settles, because a UI that keeps saying
      // "checking…" after it has given up is the same lie in a friendlier voice.
      setState({ kind: "unknown", retrying: delay !== undefined });
      if (delay === undefined) return;
      retryAttempt.current += 1;
      clearRetry();
      retryTimer.current = setTimeout(() => {
        void refetchRef.current();
      }, delay);
    }
  }, [sessionKey, clearRetry]);

  // The timer calls the LATEST refetch without making it self-referential.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    setState(null);
    setRows(null);
    setDefinitiveFail(null);
    setNotApplied(null);
    clearRetry();
    retryAttempt.current = 0;
    void refetch();
  }, [refetch, clearRetry]);

  // Unmount: never leave a timer pointing at a dead component.
  useEffect(() => clearRetry, [clearRetry]);

  // Recovery: a read that failed while the gateway was down/authenticating
  // leaves {kind:"unknown"} — honest, but the hook had no path OUT of it
  // short of a full page reload (operator-reported 2026-08-30: the chip stuck
  // on "Nível indisponível" after a runtime bounce). A reconnect also
  // invalidates whatever was read BEFORE it (a fleet roll may have changed
  // the org default). Refetch on every transition INTO "connected": the state
  // is re-read from the store of record either way, so this only ever moves
  // the UI toward truth; rows reset so stale refs don't survive a roll (the
  // chip's eager-rows effect re-fetches them in the default state).
  const connectionState = useGatewayStore((s) => s.connectionState);
  const prevConn = useRef(connectionState);
  useEffect(() => {
    const was = prevConn.current;
    prevConn.current = connectionState;
    if (connectionState === "connected" && was !== "connected") {
      setRows(null);
      // A reconnect is fresh evidence: restart the backoff rather than letting
      // an exhausted sequence keep the chip settled on a stale failure.
      clearRetry();
      retryAttempt.current = 0;
      void refetch();
    }
  }, [connectionState, refetch, clearRetry]);

  /** Lazy — the popover fetches rows when it opens. Informational only. */
  const fetchRows = useCallback(async () => {
    if (!sessionKey) return;
    try {
      const res = await rpc().call<{ ok: true; levels: unknown[] }>(
        "lx.get_chat_levels",
        { sessionKey },
      );
      if (alive.current) setRows(reduceRows(res));
    } catch {
      if (alive.current) setRows(null); // rows are optional decoration; absence ≠ fatal
    }
  }, [sessionKey]);

  /**
   * Apply a pick. Every level SETS — ⚖ R-C (contract v1.6 §4): NORMAL is a
   * REAL set (luna@medium), revising the v1.4 "NORMAL clears" mechanic
   * (B-v4); `lx.clear_chat_level` now means "return to the FAST default" and
   * the popover deliberately exposes no clear (an untouched session already
   * IS FAST — picking FAST serves the same thing). Returns `true` when
   * applied, `false` on the retryable class (caller toasts); a DEFINITIVE
   * break lands in `definitiveFail` for the L-9 gate and also returns
   * `false`.
   */
  const pick = useCallback(
    async (level: ConciergeMode): Promise<boolean> => {
      if (!sessionKey || busy) return false;
      setBusy(true);
      try {
        const res = await rpc().call<unknown>("lx.set_chat_level", {
          sessionKey,
          level,
          originSurface: "lexide",
        });
        if (isDefinitiveFail(res)) {
          if (alive.current) setDefinitiveFail(res);
          return false;
        }
        // ⚖ v1.7 §D (ii): the seam verified the upstream patch, it did NOT
        // apply, so it refused to write the store. State is untouched — the
        // chip keeps showing the previous, still-true level — and the dialog
        // says the pick did not take effect. NEVER the L-9 gate (that means
        // catalog-broken), never a bare retry line (the call answered fine).
        if (isPinNotApplied(res)) {
          if (alive.current) setNotApplied(res);
          return false;
        }
        const readback = (res as { readback?: unknown } | null)?.readback;
        if (alive.current) {
          setDefinitiveFail(null);
          setNotApplied(null);
          setState(reduceChatLevelReadback(readback));
        }
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[chat-level] pick failed (retryable class)", err);
        return false; // retryable — caller toasts, state keeps its last truth
      } finally {
        if (alive.current) setBusy(false);
      }
    },
    [sessionKey, busy],
  );

  const ackSelfHeal = useCallback(async () => {
    if (!sessionKey) return;
    try {
      await rpc().call<unknown>("lx.ack_chat_level_selfheal", { sessionKey });
    } catch {
      /* ack is best-effort; the notice re-arrives on the next read */
    }
    void refetch();
  }, [sessionKey, refetch]);

  return {
    state,
    rows,
    definitiveFail,
    notApplied,
    busy,
    refetch,
    fetchRows,
    pick,
    ackSelfHeal,
  };
}
